import { Button } from "reactstrap";
import { useState } from "react";
import dayjs from "dayjs";

import useFeedbackConversations from "../../api/api-hooks/feedback/useFeedbackConversations";
import useFeedbackConversation from "../../api/api-hooks/feedback/useFeedbackConversation";
import useSendFeedback from "../../api/api-hooks/feedback/useSendFeedback";
import CenteredSpinner from "../../elements/CenteredSpinner";
import { DefaultFields } from "../../types/GlobalInterfaces";
import { BriePages, SetBriePage } from "./BrieChatMenu";
import { UppyFileInterface } from "../Inputs/UppyFile";
import useUser from "../../api/api-hooks/auth/useUser";
import ConversationCard from "./ConversationCard";
import BrieVoiceInput from "./BrieVoiceInput";
import StrUtils from "../../utils/StrUtils";
import BrieChat from "./BrieChat";

export interface FeedbackMessagePayload {
  id: string;
  Sender: {
    id: string;
    Name: string;
    _ModuleAPIName: string;
    _id: string;
  };
  ConversationID?: string;
  Message: string;
  Attachments?: any[];
  browserData: string;
  lastVisitedTenant: string;
}

export interface FeedbackMessage {
  id: string;
  Sender?: {
    id: string;
    Name: string;
    _ModuleAPIName: string;
    _id: string;
  };
  Receiver?: {
    id: string;
    Name: string;
    _ModuleAPIName: string;
    _id: string;
  };
  Message: string;
  Attachments?: UppyFileInterface[];
}

export interface FeedbackConversation extends DefaultFields {
  Messages?: FeedbackMessage[];
  MetaData?: any;
  BrowserData?: string;
  LastVisitedTenant?: string;
  DeveloperNotes?: any;
  Status?: "open" | "in-progress" | "hold" | "resolved";
  AssignedTo?: string | null;
  Priority?: "low" | "normal" | "high";
}

export interface FeedbackConversationCard {
  _id: string;
  LastMessage: {
    Message: string;
  };
  LastUpdated: string;
}

const BrieChatFeedback = ({
  page,
  setPage,
}: {
  page: BriePages;
  setPage: SetBriePage;
}) => {
  const { user } = useUser();
  const { conversations, conversationsLoading } = useFeedbackConversations();
  const { sendFeedback, sendingFeedback } = useSendFeedback();
  const [conversationID, setConversationID] = useState<any>();
  const [inputText, setInputText] = useState("");
  const [attachments, setAttachments] = useState<UppyFileInterface[]>([]);
  const isVoice = page.startsWith("voice");

  const { conversation, conversationLoading } = useFeedbackConversation(
    conversationID !== "new" ? conversationID : ""
  );

  if (conversationsLoading) {
    return <CenteredSpinner size="xl" />;
  }

  return (
    <div className=" tw:w-full tw:h-full">
      {(conversationsLoading || conversationLoading) && (
        <CenteredSpinner size="xl" />
      )}

      {!conversationsLoading && !isVoice && conversationID && (
        <BrieChat
          attachments={attachments}
          setAttachments={setAttachments}
          messages={conversation?.Messages || []}
          topMessage={
            <div className="text-center mt--1 mb-2 text-xs">
              If you need help with something please{" "}
              <a
                href="https://www.growthcloud.com/support"
                className="text-primary font-weight-500 text-underline--hover cursor-pointer"
              >
                contact support.
              </a>
              <br /> We will respond to questions via Email ASAP.
            </div>
          }
          isFeedback
          onSend={sendFeedbackToServer}
          disableInput={sendingFeedback}
          inputText={inputText}
          setInputText={setInputText}
          onUseVoice={() => setPage("voice-chat-feedback")}
          conversationID={conversationID}
        />
      )}
      {!conversationID && (
        <>
          {conversations?.map((conversation) => (
            <div key={conversation._id} className="tw:w-full tw:px-2">
              <ConversationCard
                title={conversation?.LastMessage?.Message}
                description={dayjs(conversation.LastUpdated).fromNow()}
                icon={
                  <i className="mdi mdi-message-text text-primary tw:text-2xl" />
                }
                onClick={() => setConversationID(conversation._id)}
              />
            </div>
          ))}
          <div className="tw:px-2 tw:py-2 tw:flex tw:justify-center tw:items-center">
            <Button
              className="tw:w-full tw:p-0.5 tw:mx-3 tw:my-1"
              color="primary"
              onClick={handleNewConversation}
            >
              <i className="mdi mdi-plus-thick tw:text-xl"></i>
            </Button>
          </div>
        </>
      )}
      {!conversationsLoading && isVoice && (
        <BrieVoiceInput onSuccess={handleTranscriptSuccess} />
      )}
    </div>
  );

  function sendFeedbackToServer() {
    const feedbackMessage: FeedbackMessagePayload = {
      id: StrUtils.uuidv4(),
      Sender: {
        id: user?.id,
        Name: user?.Name,
        _ModuleAPIName: "People",
        _id: user?._id,
      },
      ConversationID: conversationID,
      Message: inputText,
      Attachments: attachments,
      browserData: navigator.userAgent,
      lastVisitedTenant: user?.LastVisitedTenant,
    };
    setAttachments([]);
    sendFeedback({
      message: feedbackMessage,
      onSuccess: (data: any) => {
        setConversationID(data.id);
      },
    });
  }

  function handleTranscriptSuccess(transcript: string) {
    setInputText(transcript);
    setPage("chat-feedback");
  }

  function handleNewConversation() {
    setConversationID("new");
  }
};

export default BrieChatFeedback;
